/**
 * Event System API Service (Fixed Version)
 * Manages API calls for the event system (templates, flows, actions, devices)
 * With improved tenant ID handling and error recovery
 */
import { devLog } from '../utils/devLog';
import { get, post, put, del } from './apiService';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { scopedTenantId } from '../pages/Admin/dealerAdminSession';

// Use API_ROUTES from the centralized constants file
// This ensures consistency across the application

// Fallback settings - determines if we try the API first or just use Firestore
// Set this to true now that the backend API supports tenant filtering
const USE_API_FIRST = true;

/**
 * Helper function to safely get tenant ID from various sources
 * @returns {string|null} - Valid tenant ID or null
 */
const getSafeTenantId = providedTenantId => {
  const dealerTenantId = scopedTenantId('');
  if (dealerTenantId) return dealerTenantId;

  // First try the provided tenant ID
  if (
    providedTenantId &&
    typeof providedTenantId === 'string' &&
    providedTenantId.trim().length > 0
  ) {
    return providedTenantId.trim();
  }

  // Then try localStorage if available
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedTenantId = window.localStorage.getItem('selectedTenantId');
      if (storedTenantId && storedTenantId.trim().length > 0) {
        devLog(`Using tenant ID from localStorage: ${storedTenantId}`);
        return storedTenantId.trim();
      }
    }
  } catch (e) {
    console.warn('Error accessing localStorage:', e);
  }

  return null;
};

/**
 * Helper function to append tenantId to API URL if provided
 * @param {string} baseUrl - The base API URL
 * @param {string} tenantId - Optional tenant ID to filter by
 * @param {Object} additionalParams - Optional additional query parameters
 * @returns {string} URL with query parameters
 */
const appendTenantId = (baseUrl, tenantId, additionalParams = {}) => {
  // Validate tenant ID - ensure it's not null, undefined, or empty string
  const validTenantId =
    tenantId && tenantId.trim && tenantId.trim().length > 0 ? tenantId.trim() : null;

  // Split existing query (if present) so we can merge parameters safely
  const [path, existingQuery = ''] = baseUrl.split('?');
  const params = new URLSearchParams(existingQuery);

  // If no valid tenant ID, no additional params, and no existing query, return base URL
  if (!validTenantId && !Object.keys(additionalParams).length && !existingQuery) {
    return baseUrl;
  }

  // Only add tenantId if it's valid and not already present
  if (validTenantId) {
    if (!params.has('tenantId')) {
      params.set('tenantId', validTenantId);
    }
  }

  // Add/override any additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, value.toString());
    }
  });

  const queryString = params.toString();
  const fullUrl = queryString ? `${path}?${queryString}` : path;
  return fullUrl;
};

/**
 * Helper function to add tenantId to request body if provided
 * @param {Object} data - The request body data
 * @param {string} tenantId - Optional tenant ID to include
 * @returns {Object} Data with tenantId added
 */
const addTenantToBody = (data, tenantId) => {
  const safeTenant = getSafeTenantId(tenantId);
  const body = { ...data };
  // Preserve explicit body.tenantId if already set; otherwise add safe tenant
  if (!body.tenantId && safeTenant) {
    body.tenantId = safeTenant;
  }
  return body;
};

/**
 * Map UI action type values to backend-expected types
 * UI types: 'sms' | 'email' | 'sip' | 'webhook' | 'push'
 * Backend: 'twilio_sms' | 'twilio_email' | 'twilio_sip' | 'webhook' | 'rest'
 */
const UI_TO_BACKEND_ACTION_TYPE = {
  sms: 'twilio_sms',
  email: 'twilio_email',
  sip: 'twilio_sip',
  voice: 'voice_call',
  webhook: 'webhook',
  device: 'device_action',
  desktop: 'desktop_action',
  // 'push' is not supported by backend actions API
};

/**
 * Normalize action payload for backend create/update
 * - Map type from UI value to backend value when applicable
 * - Map webhookUrl -> url for webhook actions
 * - Map isActive -> enabled when present
 */
const normalizeActionPayloadForBackend = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  const data = { ...payload };

  // Map UI type to backend type if needed
  if (data.type && typeof data.type === 'string') {
    const lowerType = data.type.toLowerCase();
    // Normalize push-like types
    if (lowerType === 'push_notification') {
      data.type = 'push';
    }
    if (UI_TO_BACKEND_ACTION_TYPE[lowerType]) {
      data.type = UI_TO_BACKEND_ACTION_TYPE[lowerType];
    }
  }

  // Normalize enabled flag from isActive
  if (typeof data.isActive === 'boolean' && typeof data.enabled !== 'boolean') {
    data.enabled = data.isActive;
  }

  // Normalize webhook-specific fields
  if (data.type === 'webhook') {
    if (!data.url && typeof data.webhookUrl === 'string' && data.webhookUrl.trim().length > 0) {
      data.url = data.webhookUrl.trim();
    }
  }

  // Normalize SIP params from UI fields
  if (data.type === 'twilio_sip') {
    const params = { ...(data.parameters || {}) };
    if (typeof data.messageTemplate === 'string') params.message = data.messageTemplate;
    if (typeof data.sipTo === 'string' && data.sipTo.trim()) params.to = data.sipTo.trim();
    data.parameters = params;
  }

  // Normalize Voice Call params from UI fields (ensures Caller ID is sent)
  if (data.type === 'voice_call') {
    const params = { ...(data.parameters || {}) };
    if (typeof data.messageTemplate === 'string') params.message = data.messageTemplate;
    if (typeof data.voiceTo === 'string' && data.voiceTo.trim()) params.to = data.voiceTo.trim();
    if (typeof data.voiceFrom === 'string' && data.voiceFrom.trim()) params.from = data.voiceFrom.trim();
    if (typeof data.audioFileUrl === 'string' && data.audioFileUrl.trim()) params.audioFileUrl = data.audioFileUrl.trim();
    if (typeof data.ttsVoice === 'string' && data.ttsVoice.trim()) params.ttsVoice = data.ttsVoice.trim();
    if (typeof data.language === 'string' && data.language.trim()) params.language = data.language.trim();
    data.parameters = params;
  }

  return data;
};

// Event Templates API
export const templateApi = {
  getAll: async (tenantId = null, activeOnly = false) => {
    if (!USE_API_FIRST) {
      // Skip API call and return null to trigger Firestore fallback
      devLog('Skipping API call to /templates (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = getSafeTenantId(tenantId);
    if (!safeTenantId) {
      console.warn('No valid tenant ID for templateApi.getAll');
      return { success: true, data: [] };
    }

    const url = appendTenantId(API_ROUTES.EVENT_TEMPLATES, safeTenantId, {
      active_only: activeOnly,
    });
    devLog(`Fetching templates with tenant ID: ${safeTenantId}`);

    try {
      return await get(url);
    } catch (error) {
      console.error(`Error fetching templates: ${error.message}`);
      return { success: true, data: [] };
    }
  },

  getById: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to /templates/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.EVENT_TEMPLATES}/${id}`, tenantId);
    return get(url);
  },

  create: async (templateData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to create template (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(templateData, tenantId);
    return post(API_ROUTES.EVENT_TEMPLATES, data);
  },

  update: async (id, templateData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to update template ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(templateData, tenantId);
    return put(`${API_ROUTES.EVENT_TEMPLATES}/${id}`, data);
  },

  delete: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to delete template ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.EVENT_TEMPLATES}/${id}`, tenantId);
    return del(url);
  },
};

// Event Flows API
export const flowApi = {
  getAll: async (tenantId = null, templateId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /flows (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = getSafeTenantId(tenantId);
    devLog(`Using tenantId: ${safeTenantId} for flows fetch`);

    // If no valid tenant ID, return empty data
    if (!safeTenantId) {
      return { success: true, data: [] };
    }

    try {
      // Construct URL with tenant ID parameter and optional templateId
      const url = appendTenantId(API_ROUTES.FLOWS, safeTenantId, { templateId });
      // Make API request
      return await get(url);
    } catch (error) {
      // Return empty data instead of throwing to prevent retry loops
      return { success: true, data: [] };
    }
  },

  getById: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.FLOWS}/${id}`, tenantId);
    return get(url);
  },

  create: async (flowData, tenantId = null) => {
    if (!USE_API_FIRST) {
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(flowData, tenantId);
    return post(API_ROUTES.FLOWS, data);
  },

  update: async (id, flowData, tenantId = null) => {
    if (!USE_API_FIRST) {
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(flowData, tenantId);
    return put(`${API_ROUTES.FLOWS}/${id}`, data);
  },

  delete: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to delete flow ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.FLOWS}/${id}`, tenantId);
    return del(url);
  },
};

// Actions API (for event system actions)
export const actionApi = {
  getAll: async (tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /actions (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = getSafeTenantId(tenantId);
    devLog(`Using tenantId: ${safeTenantId} for actions fetch`);

    // If no valid tenant ID, return empty data
    if (!safeTenantId) {
      console.warn('No valid tenant ID for actionApi.getAll');
      return { success: true, data: [] };
    }

    try {
      // Construct URL with tenant ID parameter
      const url = appendTenantId(API_ROUTES.ACTIONS, safeTenantId);
      devLog(`Fetching actions with tenant ID: ${safeTenantId}`);

      // Make API request
      return await get(url);
    } catch (error) {
      console.warn(`Error fetching actions (this may be expected): ${error.message}`);
      // Return empty data instead of throwing to prevent retry loops
      return { success: true, data: [] };
    }
  },

  getById: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to /actions/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.ACTIONS}/${id}`, tenantId);
    return get(url);
  },

  create: async (actionData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to create action (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const dataWithTenant = addTenantToBody(actionData, tenantId);
    const normalized = normalizeActionPayloadForBackend(dataWithTenant);
    return post(API_ROUTES.ACTIONS, normalized);
  },

  update: async (id, actionData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to update action ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const dataWithTenant = addTenantToBody(actionData, tenantId);
    const normalized = normalizeActionPayloadForBackend(dataWithTenant);
    return put(`${API_ROUTES.ACTIONS}/${id}`, normalized);
  },

  delete: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to delete action ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.ACTIONS}/${id}`, tenantId);
    return del(url);
  },

  dispatch: async (actionData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to dispatch action (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(actionData, tenantId);
    return post(API_ROUTES.ACTIONS_DISPATCH, data);
  },
};

// Devices API (for test devices)
export const deviceApi = {
  getAll: async (tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /devices (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = getSafeTenantId(tenantId);
    devLog(`Using tenantId: ${safeTenantId} for devices fetch`);

    // If no valid tenant ID, return empty data
    if (!safeTenantId) {
      console.warn('No valid tenant ID for deviceApi.getAll');
      return { success: true, data: [] };
    }

    try {
      // Construct URL with tenant ID parameter
      const url = appendTenantId(API_ROUTES.DEVICES, safeTenantId);
      devLog(`Fetching devices with tenant ID: ${safeTenantId}`);

      // Make API request
      const result = await get(url);
      return result;
    } catch (error) {
      console.warn(`Error fetching devices (this may be expected): ${error.message}`);
      // Return empty data instead of throwing to prevent retry loops
      return { success: true, data: [] };
    }
  },

  getById: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to /devices/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.DEVICES}/${id}`, tenantId);
    return get(url);
  },

  create: async (deviceData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to create device (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(deviceData, tenantId);
    return post(API_ROUTES.DEVICES, data);
  },

  update: async (id, deviceData, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to update device ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(deviceData, tenantId);
    return put(`${API_ROUTES.DEVICES}/${id}`, data);
  },

  delete: async (id, tenantId = null) => {
    if (!USE_API_FIRST) {
      devLog(`Skipping API call to delete device ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.DEVICES}/${id}`, tenantId);
    return del(url);
  },
};

// Alert API (for testing and triggering alerts)
export const alertApi = {
  sendAlert: async alertData => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /alerts/send (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    return post(API_ROUTES.ALERTS_SEND, alertData);
  },

  testAlert: async testData => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /alert/test (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    return post(API_ROUTES.ALERT_TEST, testData);
  },

  acknowledgeAlert: async ({ tenantId, alertId, userId, notes } = {}) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /alerts/acknowledge (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const payload = {
      tenantId: getSafeTenantId(tenantId),
      alertId,
    };
    if (userId) payload.userId = userId;
    if (notes) payload.notes = notes;
    return post(API_ROUTES.ALERTS_ACKNOWLEDGE, payload);
  },

  getAlertLogs: async (filters = {}) => {
    if (!USE_API_FIRST) {
      devLog('Skipping API call to /alert-logs (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    // Ensure tenantId present and build query string
    const queryParams = new URLSearchParams();
    const safeTenant = getSafeTenantId(filters?.tenantId);
    if (safeTenant) queryParams.append('tenantId', safeTenant);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== 'tenantId') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return get(`${API_ROUTES.ALERT_LOGS}${queryString ? `?${queryString}` : ''}`);
  },
}
